import { useState, useEffect } from 'react';
import apiService from '../../../services/api';

interface MetaTitleGeneratorDetailProps {
  onBack: () => void;
  darkMode?: boolean;
  lockedKeywords?: string[];
}

interface TagRow {
  tag: string;
  charLimit: string;
  keyRule: string;
  seoImpact: string;
}

interface AuditIssue {
  id: string;
  page: string;
  issue: string;
  severity: 'critical' | 'warning' | 'info';
  tag: string;
  detail: string;
}

export default function MetaTitleGenerator({ onBack, darkMode, lockedKeywords }: MetaTitleGeneratorDetailProps) {
  // Process flow tabs removed
  const [expandedIssue, setExpandedIssue] = useState<string | null>(null);
  const [scanStarted, setScanStarted] = useState(false);
  const [scanComplete, setScanComplete] = useState(false);
  const [pageResults, setPageResults] = useState<any[]>([]);
  const [selectedPage, setSelectedPage] = useState<string>('');
  const [websiteState, setWebsiteState] = useState<'existing' | 'new' | null>(null);
  const [isCompleted, setIsCompleted] = useState(false);
  const [markingComplete, setMarkingComplete] = useState(false);
  const [isLoadingCache, setIsLoadingCache] = useState(true);
  const [scanProgress, setScanProgress] = useState(0);

  const [auditIssues, setAuditIssues] = useState<AuditIssue[]>([]);

  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (scanStarted && !scanComplete) {
      setScanProgress(0);
      interval = setInterval(() => {
        setScanProgress(prev => {
          if (prev >= 90) return prev;
          return prev + Math.floor(Math.random() * 5) + 1; // Increment by 1-5%
        });
      }, 500);
    } else if (scanComplete) {
      setScanProgress(100);
    }
    return () => clearInterval(interval);
  }, [scanStarted, scanComplete]);

  useEffect(() => {
    const fetchStatusAndCache = async () => {
      const userId = localStorage.getItem('user_id');
      if (!userId) {
        setIsLoadingCache(false);
        return;
      }
      try {
        const res = await apiService.getMetaTitleGeneratorStatus(userId);
        if (res && res.is_completed) {
          setIsCompleted(true);
        }
        
        // Fetch cached results if they exist (without forcing rescan)
        const cacheRes = await apiService.auditWebsiteMeta(userId, 10, false);
        const results = cacheRes?.data?.results || cacheRes?.results;
        const wState = cacheRes?.data?.website_state || 'existing';
        setWebsiteState(wState);
        
        if (results && results.length > 0) {
          const newIssues: AuditIssue[] = [];
          setPageResults(results);
          results.forEach((pageResult: any) => {
            if (pageResult.tags) {
              pageResult.tags.forEach((tag: any) => {
                if (tag.status !== 'ok') {
                  newIssues.push({
                    id: Math.random().toString(36).substr(2, 9),
                    page: pageResult.page_url,
                    issue: tag.issue || `Issue with ${tag.tag_name}`,
                    severity: tag.status,
                    tag: tag.tag_name,
                    detail: tag.current_value || 'No value found'
                  });
                }
              });
            }
          });
          setAuditIssues(newIssues);
          setScanComplete(true);
          setScanStarted(true);
        }
      } catch (err) {
        console.error("Failed to fetch meta title generator status", err);
      } finally {
        setIsLoadingCache(false);
      }
    };
    fetchStatusAndCache();
  }, []);
  
  // Editor States
  const [editorTitle, setEditorTitle] = useState('');
  const [editorDesc, setEditorDesc] = useState('');
  const [editorOgImage, setEditorOgImage] = useState('');
  
  // Additional Tags
  const [editorMetaRobots, setEditorMetaRobots] = useState('');
  const [editorCanonical, setEditorCanonical] = useState('');
  const [editorViewport, setEditorViewport] = useState('');
  const [editorOgTitle, setEditorOgTitle] = useState('');
  const [editorOgDesc, setEditorOgDesc] = useState('');
  const [editorTwitterCard, setEditorTwitterCard] = useState('');
  const [editorHreflang, setEditorHreflang] = useState('');
  
  const [presentTags, setPresentTags] = useState<Record<string, boolean>>({});
  
  const [snippetCopied, setSnippetCopied] = useState(false);
  const [generatingTags, setGeneratingTags] = useState<Record<string, boolean>>({});

  const startScan = async () => {
    setScanStarted(true);
    setScanComplete(false);
    try {
      const userId = localStorage.getItem('user_id') || '';
      // Force rescan on button click
      const response = await apiService.auditWebsiteMeta(userId, 10, true);
      const newIssues: AuditIssue[] = [];
      const results = response?.data?.results || response?.results;
      const wState = response?.data?.website_state || 'existing';
      setWebsiteState(wState);
      
      if (response && response.is_completed) {
        setIsCompleted(true);
      } else {
        setIsCompleted(false);
      }
      
      if (results) {
        setPageResults(results);
        results.forEach((pageResult: any) => {
          if (pageResult.tags) {
            pageResult.tags.forEach((tag: any) => {
              if (tag.status !== 'ok') {
                newIssues.push({
                  id: Math.random().toString(36).substr(2, 9),
                  page: pageResult.page_url,
                  issue: tag.issue || `Issue with ${tag.tag_name}`,
                  severity: tag.status,
                  tag: tag.tag_name,
                  detail: tag.current_value || 'No value found'
                });
              }
            });
          }
        });
        
        // Auto-select first page if available
        if (results.length > 0) {
          handlePageSelect(results[0].page_url, results[0].tags, wState);
        }
      }
      setAuditIssues(newIssues);
    } catch (error) {
      console.error("Error scanning meta tags:", error);
    } finally {
      setScanComplete(true);
    }
  };

  const handleMarkComplete = async () => {
    const userId = localStorage.getItem('user_id') || '';
    setMarkingComplete(true);
    try {
      // Create audit_data from current state to save edits
      const currentAuditData = {
        website_state: websiteState,
        results: pageResults.map(p => {
          if (p.page_url === selectedPage) {
            // Update the tags with current editor values for the selected page
            const updatedTags = [...(p.tags || [])];
            const updateTag = (name: string, val: string) => {
              const idx = updatedTags.findIndex(t => t.tag_name === name || t.tag_name === `<${name}>` || t.tag_name.replace(':', '_') === name.replace(':', '_'));
              if (idx !== -1) {
                updatedTags[idx] = { ...updatedTags[idx], current_value: val, status: 'ok' };
              } else if (val) {
                updatedTags.push({ tag_name: name, current_value: val, status: 'ok' });
              }
            };
            updateTag('title', editorTitle);
            updateTag('meta_description', editorDesc);
            updateTag('og:image', editorOgImage);
            updateTag('meta_robots', editorMetaRobots);
            updateTag('canonical', editorCanonical);
            updateTag('viewport', editorViewport);
            updateTag('og:title', editorOgTitle);
            updateTag('og:description', editorOgDesc);
            updateTag('twitter:card', editorTwitterCard);
            updateTag('hreflang', editorHreflang);
            return { ...p, tags: updatedTags };
          }
          return p;
        })
      };
      
      await apiService.markMetaTitleGeneratorComplete(userId, currentAuditData);
      setIsCompleted(true);
    } catch (err) {
      console.error(err);
    } finally {
      setMarkingComplete(false);
    }
  };

  const handlePageSelect = (pageUrl: string, tags?: any[], forceState?: string) => {
    setSelectedPage(pageUrl);
    const pageTags = tags || pageResults.find(p => p.page_url === pageUrl)?.tags || [];
    
    const titleTag = pageTags.find((t: any) => t.tag_name === '<title>' || t.tag_name === 'title');
    const descTag = pageTags.find((t: any) => t.tag_name === 'Meta Description' || t.tag_name === 'meta_description' || t.tag_name === 'description');
    const ogImageTag = pageTags.find((t: any) => t.tag_name === 'og:image' || t.tag_name === 'og_image');
    const robotsTag = pageTags.find((t: any) => t.tag_name === 'meta_robots' || t.tag_name === 'robots' || t.tag_name === 'Meta Robots');
    const canonicalTag = pageTags.find((t: any) => t.tag_name === 'canonical' || t.tag_name === 'Canonical');
    const viewportTag = pageTags.find((t: any) => t.tag_name === 'viewport' || t.tag_name === 'Viewport');
    const ogTitleTag = pageTags.find((t: any) => t.tag_name === 'og_title' || t.tag_name === 'og:title');
    const ogDescTag = pageTags.find((t: any) => t.tag_name === 'og_description' || t.tag_name === 'og:description');
    const twitterCardTag = pageTags.find((t: any) => t.tag_name === 'twitter_card' || t.tag_name === 'twitter:card');
    const hreflangTag = pageTags.find((t: any) => t.tag_name === 'hreflang');
    
    setEditorTitle(titleTag?.current_value || '');
    setEditorDesc(descTag?.current_value || '');
    setEditorOgImage(ogImageTag?.current_value || '');
    setEditorMetaRobots(robotsTag?.current_value || '');
    setEditorCanonical(canonicalTag?.current_value || '');
    setEditorViewport(viewportTag?.current_value || '');
    setEditorOgTitle(ogTitleTag?.current_value || '');
    setEditorOgDesc(ogDescTag?.current_value || '');
    setEditorTwitterCard(twitterCardTag?.current_value || '');
    setEditorHreflang(hreflangTag?.current_value || '');
    
    // Determine which tags are present
    const currentState = forceState || websiteState;
    setPresentTags({
      title: currentState === 'new' || pageTags.some((t: any) => t.tag_name === 'title' || t.tag_name === '<title>'),
      desc: currentState === 'new' || pageTags.some((t: any) => t.tag_name === 'meta_description' || t.tag_name === 'Meta Description' || t.tag_name === 'description'),
      ogImage: currentState === 'new' || pageTags.some((t: any) => t.tag_name === 'og_image' || t.tag_name === 'og:image'),
      robots: currentState === 'new' || pageTags.some((t: any) => t.tag_name === 'meta_robots' || t.tag_name === 'robots' || t.tag_name === 'Meta Robots'),
      canonical: currentState === 'new' || pageTags.some((t: any) => t.tag_name === 'canonical' || t.tag_name === 'Canonical'),
      viewport: currentState === 'new' || pageTags.some((t: any) => t.tag_name === 'viewport' || t.tag_name === 'Viewport'),
      ogTitle: currentState === 'new' || pageTags.some((t: any) => t.tag_name === 'og_title' || t.tag_name === 'og:title'),
      ogDesc: currentState === 'new' || pageTags.some((t: any) => t.tag_name === 'og_description' || t.tag_name === 'og:description'),
      twitterCard: currentState === 'new' || pageTags.some((t: any) => t.tag_name === 'twitter_card' || t.tag_name === 'twitter:card'),
      hreflang: currentState === 'new' || pageTags.some((t: any) => t.tag_name === 'hreflang')
    });
  };

  const handleGenerateTag = async (tagName: string) => {
    const userId = localStorage.getItem('user_id') || '';
    if (!selectedPage || !userId) return;

    setGeneratingTags(prev => ({ ...prev, [tagName]: true }));

    try {
      let currentValue = '';
      if (tagName === 'title') currentValue = editorTitle;
      else if (tagName === 'meta_description') currentValue = editorDesc;
      else if (tagName === 'meta_robots') currentValue = editorMetaRobots;
      else if (tagName === 'canonical') currentValue = editorCanonical;
      else if (tagName === 'viewport') currentValue = editorViewport;
      else if (tagName === 'og_title') currentValue = editorOgTitle;
      else if (tagName === 'og_description') currentValue = editorOgDesc;
      else if (tagName === 'og_image') currentValue = editorOgImage;
      else if (tagName === 'twitter_card') currentValue = editorTwitterCard;
      else if (tagName === 'hreflang') currentValue = editorHreflang;

      const response = await apiService.regenerateMetaTag({
        user_id: userId,
        page_url: selectedPage,
        tag_name: tagName,
        current_value: currentValue,
        current_title: editorTitle // context for generating description
      });

      if (response && response.data && response.data.suggested_value) {
        const val = response.data.suggested_value;
        if (tagName === 'title') setEditorTitle(val);
        else if (tagName === 'meta_description') setEditorDesc(val);
        else if (tagName === 'meta_robots') setEditorMetaRobots(val);
        else if (tagName === 'canonical') setEditorCanonical(val);
        else if (tagName === 'viewport') setEditorViewport(val);
        else if (tagName === 'og_title') setEditorOgTitle(val);
        else if (tagName === 'og_description') setEditorOgDesc(val);
        else if (tagName === 'og_image') setEditorOgImage(val);
        else if (tagName === 'twitter_card') setEditorTwitterCard(val);
        else if (tagName === 'hreflang') setEditorHreflang(val);
      }
    } catch (error) {
      console.error(`Error generating ${tagName}:`, error);
      alert(`Failed to generate ${tagName}. Please try again.`);
    } finally {
      setGeneratingTags(prev => ({ ...prev, [tagName]: false }));
    }
  };

  const renderGenerateButton = (tagName: string) => (
    <button 
      onClick={() => handleGenerateTag(tagName)}
      disabled={!selectedPage || generatingTags[tagName]}
      className={`text-[11px] font-medium cursor-pointer whitespace-nowrap flex items-center ${
        !selectedPage || generatingTags[tagName]
          ? 'text-gray-400 cursor-not-allowed'
          : 'text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300'
      }`}
    >
      {generatingTags[tagName] ? (
        <><i className="ri-loader-4-line animate-spin mr-0.5"></i> Generating...</>
      ) : (
        <><i className="ri-sparkling-line mr-0.5"></i> AI Generate</>
      )}
    </button>
  );

  const tagsCovered: TagRow[] = [
    {
      tag: '<title>',
      charLimit: '50–60 chars',
      keyRule: 'Primary keyword early; unique per page; brand at end',
      seoImpact: 'Rankings + CTR'
    },
    {
      tag: 'Meta Description',
      charLimit: '140–160 chars',
      keyRule: 'Benefit-driven; include keyword naturally; no stuffing',
      seoImpact: 'CTR'
    },
    {
      tag: 'Meta Robots',
      charLimit: '—',
      keyRule: 'index/follow on priority pages; audit for accidental noindex',
      seoImpact: 'Crawl & indexation'
    },
    {
      tag: 'Canonical',
      charLimit: '—',
      keyRule: 'Self-referencing on every indexable page; resolve duplicates',
      seoImpact: 'Duplicate prevention'
    },
    {
      tag: 'Viewport',
      charLimit: '—',
      keyRule: 'content="width=device-width, initial-scale=1" on every page',
      seoImpact: 'Mobile indexing'
    },
    {
      tag: 'og:title',
      charLimit: '60–90 chars',
      keyRule: 'Custom per page; can differ from title tag',
      seoImpact: 'Social CTR'
    },
    {
      tag: 'og:description',
      charLimit: '100–200 chars',
      keyRule: 'Engaging; not a copy of meta description',
      seoImpact: 'Social engagement'
    },
    {
      tag: 'og:image',
      charLimit: '1200×630 px',
      keyRule: 'Relevant, high-quality; includes brand element',
      seoImpact: 'Social preview quality'
    },
    {
      tag: 'twitter:card',
      charLimit: '—',
      keyRule: 'summary_large_image preferred for content pages',
      seoImpact: 'X visibility'
    },
    {
      tag: 'hreflang',
      charLimit: '—',
      keyRule: 'Required only for multilingual sites; must be reciprocal',
      seoImpact: 'International indexing'
    },
    {
      tag: 'ALT Text',
      charLimit: '50–125 chars',
      keyRule: 'Descriptive, contextual, keyword-aware; never stuffed',
      seoImpact: 'Image SEO + accessibility'
    }
  ];


  const cardBg = darkMode ? 'bg-gray-800' : 'bg-white';
  const borderColor = darkMode ? 'border-gray-700' : 'border-gray-200';
  const textPrimary = darkMode ? 'text-white' : 'text-gray-900';
  const textSecondary = darkMode ? 'text-gray-400' : 'text-gray-500';
  const severityColors: Record<string, string> = {
    critical: 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800 text-red-700 dark:text-red-400',
    warning: 'bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800 text-amber-700 dark:text-amber-400',
    info: 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800 text-blue-700 dark:text-blue-400'
  };
  const severityDots: Record<string, string> = {
    critical: 'bg-red-500',
    warning: 'bg-amber-500',
    info: 'bg-blue-500'
  };

  const criticalCount = auditIssues.filter(i => i.severity === 'critical').length;
  const warningCount = auditIssues.filter(i => i.severity === 'warning').length;
  const infoCount = auditIssues.filter(i => i.severity === 'info').length;

  return (
    <div className="space-y-6">
      {/* Back Button */}
      <div className="mb-1">
        <button
          onClick={onBack}
          className="flex items-center text-sm font-medium text-gray-500 hover:text-blue-600 dark:text-gray-400 dark:hover:text-blue-400 transition-colors cursor-pointer"
        >
          <i className="ri-arrow-left-line mr-1.5"></i> Back
        </button>
      </div>

      <div className="flex items-start gap-4">
        <div className="w-12 h-12 rounded-xl bg-blue-50 dark:bg-blue-900/30 flex items-center justify-center flex-shrink-0 border border-blue-200 dark:border-blue-800">
          <i className="ri-code-s-slash-line text-blue-600 dark:text-blue-400 text-xl"></i>
        </div>
        <div>
          <div className="flex items-center gap-2">
            <h2 className="text-xl font-bold text-gray-900 dark:text-white">Meta Title Generator</h2>
            {isCompleted ? (
              <span className="bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 text-xs px-2.5 py-1 rounded-full font-semibold flex items-center gap-1 ml-3 border border-green-200 dark:border-green-800">
                <i className="ri-check-line"></i> Completed
              </span>
            ) : (
              <button
                onClick={handleMarkComplete}
                disabled={markingComplete || !scanComplete}
                className={`text-xs px-3 py-1.5 rounded-lg font-semibold flex items-center gap-1.5 ml-3 transition-colors border ${
                  markingComplete || !scanComplete 
                    ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed dark:bg-gray-800 dark:text-gray-500 dark:border-gray-700' 
                    : 'bg-gray-100 hover:bg-gray-200 text-gray-700 border-gray-200 cursor-pointer dark:bg-gray-800 dark:hover:bg-gray-700 dark:text-gray-300 dark:border-gray-700'
                }`}
              >
                {markingComplete ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-check-double-line"></i>}
                Mark as Complete
              </button>
            )}
            <button
              className="text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors cursor-pointer group relative"
              onClick={(e) => {
                e.stopPropagation();
                const t = document.getElementById('meta-seo-tooltip');
                if (t) {
                  t.classList.toggle('opacity-0');
                  t.classList.toggle('opacity-100');
                  setTimeout(() => {
                    t.classList.add('opacity-0');
                    t.classList.remove('opacity-100');
                  }, 4000);
                }
              }}
            >
              <i className="ri-information-line text-lg"></i>
              <div
                id="meta-seo-tooltip"
                className="absolute left-0 top-full mt-2 w-80 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-left p-4 rounded-xl shadow-xl opacity-0 pointer-events-none transition-opacity z-50 leading-relaxed"
              >
                <p className="text-sm font-semibold text-gray-900 dark:text-white mb-2 flex items-center gap-2">
                  <i className="ri-lightbulb-line text-blue-600 dark:text-blue-400"></i>
                  Intelligence Note
                </p>
                <p className="text-xs text-gray-600 dark:text-gray-400 mb-2">
                  <strong className="text-blue-600 dark:text-blue-400">For new websites</strong> — provides guided templates and best-practice defaults.
                </p>
                <p className="text-xs text-gray-600 dark:text-gray-400">
                  <strong className="text-blue-600 dark:text-blue-400">For existing websites</strong> — scans current meta implementations, surfaces gaps and errors (missing tags, duplicate titles, character overflows), and allows the user to optimise directly within the platform.
                </p>
              </div>
            </button>
          </div>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Generate, audit, and optimise the full set of HTML meta tags across every page of your website.</p>
        </div>
      </div>

      {/* Tags Covered Table */}
      <div className={`${cardBg} rounded-xl border ${borderColor} overflow-hidden`}>
        <div className="px-5 py-4 border-b border-gray-200 dark:border-gray-700">
          <h3 className={`text-sm font-semibold ${textPrimary} flex items-center gap-2`}>
            <i className="ri-price-tag-3-line text-blue-600 dark:text-blue-400"></i>
            Tags Covered
          </h3>
          <p className={`text-xs ${textSecondary} mt-0.5`}>
            Comprehensive coverage of all critical HTML meta tags for SEO and social sharing
          </p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 dark:bg-gray-700/50">
              <tr>
                <th className="text-left py-3 px-4 text-xs font-semibold text-gray-600 dark:text-gray-300">Tag</th>
                <th className="text-left py-3 px-4 text-xs font-semibold text-gray-600 dark:text-gray-300">Char Limit</th>
                <th className="text-left py-3 px-4 text-xs font-semibold text-gray-600 dark:text-gray-300">Key Rule</th>
                <th className="text-left py-3 px-4 text-xs font-semibold text-gray-600 dark:text-gray-300">SEO Impact</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
              {tagsCovered.map((row) => (
                <tr key={row.tag} className="hover:bg-gray-50 dark:hover:bg-gray-800/30 transition-colors">
                  <td className="py-3 px-4">
                    <code className="text-xs font-semibold text-blue-700 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20 px-2 py-0.5 rounded">
                      {row.tag}
                    </code>
                  </td>
                  <td className="py-3 px-4">
                    <span className="text-sm text-gray-700 dark:text-gray-300">{row.charLimit}</span>
                  </td>
                  <td className="py-3 px-4">
                    <span className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">{row.keyRule}</span>
                  </td>
                  <td className="py-3 px-4">
                    <span className="inline-flex items-center px-2.5 py-0.5 text-xs font-medium rounded-full bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-800">
                      {row.seoImpact}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Scan Controls */}
      <div className={`${cardBg} rounded-xl border ${borderColor} p-5`}>
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className={`text-sm font-semibold ${textPrimary}`}>Website Meta Tag Scan</h3>
            <p className={`text-xs ${textSecondary} mt-0.5`}>
              Crawls all accessible pages and extracts meta tags for auditing
            </p>
          </div>
          {scanComplete ? (
            <div className="flex items-center gap-3">
              <span className="px-3 py-1 text-xs font-medium rounded-full bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-800">
                <i className="ri-check-line mr-1"></i>
                Scan Complete
              </span>
              <button
                onClick={startScan}
                className="text-xs px-3 py-1 bg-blue-50 text-blue-600 hover:bg-blue-100 dark:bg-blue-900/30 dark:text-blue-400 dark:hover:bg-blue-900/50 border border-blue-200 dark:border-blue-800 rounded-lg transition-colors cursor-pointer font-medium flex items-center gap-1.5"
              >
                <i className="ri-refresh-line"></i>
                Rescan
              </button>
            </div>
          ) : scanStarted ? (
            <span className="inline-flex items-center px-3 py-1 text-xs font-medium rounded-full bg-amber-50 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 border border-amber-200 dark:border-amber-800">
              <i className="ri-loader-4-line animate-spin inline-block mr-1.5"></i>
              Scanning...
            </span>
          ) : null}
        </div>

        {isLoadingCache ? (
          <div className="flex items-center gap-3 text-sm text-gray-500 dark:text-gray-400 py-2">
            <i className="ri-loader-4-line animate-spin text-xl text-blue-500"></i>
            Checking for existing scans...
          </div>
        ) : !scanStarted && (
          <button
            onClick={startScan}
            className="px-5 py-2.5 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors whitespace-nowrap cursor-pointer"
          >
            <i className="ri-search-eye-line mr-2"></i>
            Start Website Scan
          </button>
        )}

        {scanStarted && !scanComplete && (
          <div className="space-y-3">
            <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
              <div className={`bg-blue-500 h-2 rounded-full ${scanProgress > 0 ? 'transition-all duration-500 ease-out' : ''}`} style={{ width: `${scanProgress}%` }}></div>
            </div>
            <div className="flex justify-between items-center text-xs text-gray-500 dark:text-gray-400">
              <p>Crawling pages and analyzing meta tags...</p>
              <span className="font-medium">{scanProgress}%</span>
            </div>
          </div>
        )}
      </div>

      {scanComplete && (
        <div className="space-y-6">



          {/* New Website Banner */}
          {websiteState === 'new' && (
            <div className="p-4 bg-teal-50 dark:bg-teal-900/20 border border-teal-200 dark:border-teal-800 rounded-xl">
               <h3 className="text-sm font-semibold text-teal-800 dark:text-teal-300 flex items-center"><i className="ri-rocket-line mr-2"></i> New Website Guided Setup</h3>
               <p className="text-xs text-teal-600 dark:text-teal-400 mt-1">We detected your domain is less than 6 months old. We've pre-populated your foundational pages. Let's create your meta tags from scratch using AI.</p>
            </div>
          )}

          {/* Audit Report */}
          {websiteState !== 'new' && (
            <div className="border border-gray-200 dark:border-gray-700 rounded-xl bg-white dark:bg-gray-800 overflow-hidden shadow-sm">
              <div className="px-5 py-4 border-b border-gray-200 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-800/50 flex items-center justify-between">
                <h3 className="text-sm font-semibold text-gray-900 dark:text-white flex items-center">
                  <i className="ri-bar-chart-box-line mr-2 text-blue-600 dark:text-blue-400"></i>
                  Audit Report &amp; Issue List
                </h3>
                <div className="flex items-center gap-3">
                  <div className="flex items-center gap-1.5">
                    <span className="w-2 h-2 rounded-full bg-red-500"></span>
                    <span className="text-xs text-gray-500 dark:text-gray-400">{criticalCount} critical</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <span className="w-2 h-2 rounded-full bg-amber-500"></span>
                    <span className="text-xs text-gray-500 dark:text-gray-400">{warningCount} warnings</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <span className="w-2 h-2 rounded-full bg-blue-500"></span>
                    <span className="text-xs text-gray-500 dark:text-gray-400">{infoCount} info</span>
                  </div>
                </div>
              </div>

              <div className="divide-y divide-gray-100 dark:divide-gray-700">
                {auditIssues.map((issue) => (
                  <div key={issue.id}>
                    <div
                      onClick={() => setExpandedIssue(expandedIssue === issue.id ? null : issue.id)}
                      className="px-5 py-4 flex items-center gap-4 hover:bg-gray-50 dark:hover:bg-gray-800/30 transition-colors cursor-pointer"
                    >
                      <span className={`w-2 h-2 rounded-full flex-shrink-0 ${severityDots[issue.severity]}`}></span>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className={`px-2 py-0.5 text-[11px] font-medium rounded-full ${severityColors[issue.severity]}`}>
                            {issue.severity}
                          </span>
                          <span className="text-sm font-medium text-gray-900 dark:text-white truncate">{issue.issue}</span>
                        </div>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                          <code className="text-xs bg-gray-100 dark:bg-gray-700 px-1.5 py-0.5 rounded">{issue.tag}</code>
                          <span className="mx-1.5">·</span>
                          {issue.page}
                        </p>
                      </div>
                      {expandedIssue === issue.id ? (
                        <i className="ri-arrow-up-s-line text-gray-400 text-sm"></i>
                      ) : (
                        <i className="ri-arrow-down-s-line text-gray-400 text-sm"></i>
                      )}
                    </div>

                    {expandedIssue === issue.id && (
                      <div className="px-5 pb-4 pl-[60px]">
                        <div className="p-4 rounded-lg bg-gray-50 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600">
                          <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Details</p>
                          <p className="text-sm text-gray-700 dark:text-gray-300">{issue.detail}</p>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
                {auditIssues.length === 0 && (
                  <div className="px-5 py-8 text-center text-gray-500 dark:text-gray-400 text-sm">
                    No issues found! Your meta tags look great.
                  </div>
                )}
              </div>
            </div>
          )}
      {/* Output: Per-Page Editor Preview */}
      <div className={`${cardBg} rounded-xl border ${borderColor} p-5`}>
        <h3 className={`text-sm font-semibold ${textPrimary} mb-4 flex items-center gap-2`}>
          <i className="ri-edit-box-line text-blue-600 dark:text-blue-400"></i>
          Per-Page Meta Tag Editor
        </h3>

        <div className="space-y-4">
          {/* Page Selector */}
          <div className="flex items-center gap-3 w-full">
            <label className="text-xs font-medium text-gray-500 dark:text-gray-400 whitespace-nowrap">Page:</label>
            <div className="flex-1 min-w-0">
              <select 
                value={selectedPage}
                onChange={(e) => handlePageSelect(e.target.value)}
                className="w-full px-3 py-2 text-sm bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent cursor-pointer truncate"
              >
                <option value="" disabled>Select a page...</option>
                {pageResults.map((page: any) => (
                  <option key={page.page_url} value={page.page_url}>{page.page_url}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Title Tag */}
          {presentTags.title && (
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label className="text-xs font-semibold text-gray-700 dark:text-gray-300">
                  <code className="text-teal-600 dark:text-teal-400 bg-teal-50 dark:bg-teal-900/20 px-1.5 py-0.5 rounded text-[11px]">&lt;title&gt;</code>
                </label>
                <div className="flex items-center gap-2">
                  <span className={`text-[11px] font-medium ${editorTitle.length > 60 ? 'text-red-500' : 'text-gray-400'}`}>
                    {editorTitle.length} / 60
                  </span>
                  {renderGenerateButton('title')}
                </div>
              </div>
              <input
                type="text"
                value={editorTitle}
                onChange={(e) => setEditorTitle(e.target.value)}
                placeholder="Enter meta title..."
                className="w-full px-3 py-2.5 text-sm bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          )}

          {/* Meta Description */}
          {presentTags.desc && (
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label className="text-xs font-semibold text-gray-700 dark:text-gray-300">
                  <code className="text-teal-600 dark:text-teal-400 bg-teal-50 dark:bg-teal-900/20 px-1.5 py-0.5 rounded text-[11px]">Meta Description</code>
                </label>
                <div className="flex items-center gap-2">
                  <span className={`text-[11px] font-medium ${editorDesc.length > 160 ? 'text-red-500' : 'text-gray-400'}`}>
                    {editorDesc.length} / 160
                  </span>
                  {renderGenerateButton('meta_description')}
                </div>
              </div>
              <textarea
                rows={2}
                value={editorDesc}
                onChange={(e) => setEditorDesc(e.target.value)}
                placeholder="Enter meta description..."
                className="w-full px-3 py-2.5 text-sm bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
              ></textarea>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Meta Robots */}
            {presentTags.robots && (
              <div>
                <div className="mb-1.5 flex items-center justify-between">
                  <label className="text-xs font-semibold text-gray-700 dark:text-gray-300">
                    <code className="text-teal-600 dark:text-teal-400 bg-teal-50 dark:bg-teal-900/20 px-1.5 py-0.5 rounded text-[11px]">Meta Robots</code>
                  </label>
                  {renderGenerateButton('meta_robots')}
                </div>
                <input type="text" value={editorMetaRobots} onChange={(e) => setEditorMetaRobots(e.target.value)} placeholder="e.g. index, follow" className="w-full px-3 py-2 text-sm bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
              </div>
            )}

            {/* Canonical */}
            {presentTags.canonical && (
              <div>
                <div className="mb-1.5 flex items-center justify-between">
                  <label className="text-xs font-semibold text-gray-700 dark:text-gray-300">
                    <code className="text-teal-600 dark:text-teal-400 bg-teal-50 dark:bg-teal-900/20 px-1.5 py-0.5 rounded text-[11px]">Canonical</code>
                  </label>
                  {renderGenerateButton('canonical')}
                </div>
                <input type="text" value={editorCanonical} onChange={(e) => setEditorCanonical(e.target.value)} placeholder="https://..." className="w-full px-3 py-2 text-sm bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
              </div>
            )}

            {/* Viewport */}
            {presentTags.viewport && (
              <div>
                <div className="mb-1.5 flex items-center justify-between">
                  <label className="text-xs font-semibold text-gray-700 dark:text-gray-300">
                    <code className="text-teal-600 dark:text-teal-400 bg-teal-50 dark:bg-teal-900/20 px-1.5 py-0.5 rounded text-[11px]">Viewport</code>
                  </label>
                  {renderGenerateButton('viewport')}
                </div>
                <input type="text" value={editorViewport} onChange={(e) => setEditorViewport(e.target.value)} placeholder="width=device-width..." className="w-full px-3 py-2 text-sm bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
              </div>
            )}

            {/* Twitter Card */}
            {presentTags.twitterCard && (
              <div>
                <div className="mb-1.5 flex items-center justify-between">
                  <label className="text-xs font-semibold text-gray-700 dark:text-gray-300">
                    <code className="text-teal-600 dark:text-teal-400 bg-teal-50 dark:bg-teal-900/20 px-1.5 py-0.5 rounded text-[11px]">twitter:card</code>
                  </label>
                  {renderGenerateButton('twitter_card')}
                </div>
                <input type="text" value={editorTwitterCard} onChange={(e) => setEditorTwitterCard(e.target.value)} placeholder="summary_large_image" className="w-full px-3 py-2 text-sm bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
              </div>
            )}

            {/* Hreflang */}
            {presentTags.hreflang && (
              <div className="md:col-span-2">
                <div className="mb-1.5 flex items-center justify-between">
                  <label className="text-xs font-semibold text-gray-700 dark:text-gray-300">
                    <code className="text-teal-600 dark:text-teal-400 bg-teal-50 dark:bg-teal-900/20 px-1.5 py-0.5 rounded text-[11px]">hreflang</code>
                  </label>
                  {renderGenerateButton('hreflang')}
                </div>
                <input type="text" value={editorHreflang} onChange={(e) => setEditorHreflang(e.target.value)} placeholder="en=https://..." className="w-full px-3 py-2 text-sm bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
              </div>
            )}
            
            {/* OG Title */}
            {presentTags.ogTitle && (
              <div>
                <div className="mb-1.5 flex items-center justify-between">
                  <label className="text-xs font-semibold text-gray-700 dark:text-gray-300">
                    <code className="text-teal-600 dark:text-teal-400 bg-teal-50 dark:bg-teal-900/20 px-1.5 py-0.5 rounded text-[11px]">og:title</code>
                  </label>
                  {renderGenerateButton('og_title')}
                </div>
                <input type="text" value={editorOgTitle} onChange={(e) => setEditorOgTitle(e.target.value)} placeholder="Enter og:title..." className="w-full px-3 py-2 text-sm bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
              </div>
            )}

            {/* OG Description */}
            {presentTags.ogDesc && (
              <div>
                <div className="mb-1.5 flex items-center justify-between">
                  <label className="text-xs font-semibold text-gray-700 dark:text-gray-300">
                    <code className="text-teal-600 dark:text-teal-400 bg-teal-50 dark:bg-teal-900/20 px-1.5 py-0.5 rounded text-[11px]">og:description</code>
                  </label>
                  {renderGenerateButton('og_description')}
                </div>
                <input type="text" value={editorOgDesc} onChange={(e) => setEditorOgDesc(e.target.value)} placeholder="Enter og:description..." className="w-full px-3 py-2 text-sm bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
              </div>
            )}
          </div>

          {/* SERP Preview */}
          <div className="p-4 rounded-lg bg-gray-50 dark:bg-gray-700/30 border border-gray-200 dark:border-gray-700">
            <p className="text-[11px] font-medium text-gray-400 mb-2 uppercase tracking-wide">SERP Preview</p>
            <div className={!editorTitle && !editorDesc ? "opacity-50" : ""}>
              <p className="text-lg leading-tight text-blue-700 dark:text-blue-400 hover:underline cursor-pointer truncate">
                {editorTitle || "Page Title Preview"}
              </p>
              <p className="text-sm leading-tight text-emerald-700 dark:text-emerald-400 mt-0.5 truncate">
                {selectedPage || "https://yourwebsite.com/page-url"}
              </p>
              <p className="text-sm leading-tight text-gray-600 dark:text-gray-400 mt-0.5 line-clamp-2">
                {editorDesc || "Page meta description preview will appear here once generated or entered above..."}
              </p>
            </div>
          </div>

          {/* OG Preview */}
          <div className={!editorTitle && !editorDesc && !editorOgImage ? "opacity-50" : ""}>
            <label className="text-xs font-semibold text-gray-700 dark:text-gray-300 block mb-3">
              <code className="text-teal-600 dark:text-teal-400 bg-teal-50 dark:bg-teal-900/20 px-1.5 py-0.5 rounded text-[11px]">OG Image Preview</code>
            </label>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Facebook */}
              <div>
                <p className="text-[11px] font-medium text-gray-400 mb-2">Facebook / LinkedIn</p>
                <div className="rounded-lg overflow-hidden border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
                  <div className="aspect-[1200/630] bg-gray-200 dark:bg-gray-700 flex items-center justify-center overflow-hidden">
                    {editorOgImage ? (
                      <img src={editorOgImage} alt="OG Preview" className="w-full h-full object-cover" onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} />
                    ) : (
                      <i className="ri-image-line text-4xl text-gray-400"></i>
                    )}
                  </div>
                  <div className="p-3">
                    <p className="text-[11px] uppercase text-gray-400 font-medium truncate">{selectedPage ? new URL(selectedPage).hostname : "yourwebsite.com"}</p>
                    <p className="text-sm font-semibold text-gray-900 dark:text-white mt-0.5 truncate">{editorOgTitle || editorTitle || "Page Title"}</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 line-clamp-1">{editorOgDesc || editorDesc || "Page description preview..."}</p>
                  </div>
                </div>
              </div>

              {/* Twitter/X */}
              <div>
                <p className="text-[11px] font-medium text-gray-400 mb-2">X</p>
                <div className="rounded-lg overflow-hidden border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
                  <div className="aspect-[2/1] bg-gray-200 dark:bg-gray-700 flex items-center justify-center overflow-hidden">
                    {editorOgImage ? (
                      <img src={editorOgImage} alt="X Preview" className="w-full h-full object-cover" onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} />
                    ) : (
                      <i className="ri-image-line text-4xl text-gray-400"></i>
                    )}
                  </div>
                  <div className="p-3">
                    <p className="text-sm font-semibold text-gray-900 dark:text-white truncate">{editorOgTitle || editorTitle || "Page Title"}</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 line-clamp-1">{editorOgDesc || editorDesc || "Page description preview..."}</p>
                  </div>
                </div>
              </div>

              {/* Export Snippet */}
              <div>
                <p className="text-[11px] font-medium text-gray-400 mb-2">Exportable Snippet</p>
                <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 overflow-hidden h-[calc(100%-24px)] flex flex-col">
                  <div className="px-3 py-2 bg-gray-100 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
                    <span className="text-[11px] font-medium text-gray-500">&lt;head&gt; snippet</span>
                    <button 
                      onClick={async () => {
                        const snippet = `<!-- Meta tags -->\n<title>${editorTitle || "Page Title"}</title>\n<meta name="description" content="${editorDesc || "Page description preview..."}">\n${editorMetaRobots ? `<meta name="robots" content="${editorMetaRobots}">\n` : ''}${editorViewport ? `<meta name="viewport" content="${editorViewport}">\n` : ''}${editorCanonical ? `<link rel="canonical" href="${editorCanonical}">\n` : ''}${editorHreflang ? `<link rel="alternate" hreflang="..." href="${editorHreflang}">\n` : ''}${editorTwitterCard ? `<meta name="twitter:card" content="${editorTwitterCard}">\n` : ''}${editorOgTitle ? `<meta property="og:title" content="${editorOgTitle}">\n` : ''}${editorOgDesc ? `<meta property="og:description" content="${editorOgDesc}">\n` : ''}${editorOgImage ? `<meta property="og:image" content="${editorOgImage}">\n` : ''}`.trim();
                        try {
                          await navigator.clipboard.writeText(snippet);
                          setSnippetCopied(true);
                          setTimeout(() => setSnippetCopied(false), 2000);
                        } catch (err) {
                          console.error('Failed to copy', err);
                        }
                      }}
                      className="text-[11px] font-medium text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 cursor-pointer whitespace-nowrap flex items-center relative"
                    >
                      {snippetCopied ? (
                        <><i className="ri-check-line mr-0.5"></i> Copied!</>
                      ) : (
                        <><i className="ri-file-copy-line mr-0.5"></i> Copy</>
                      )}
                    </button>
                  </div>
                  <div className="p-3 flex-1 overflow-y-auto min-w-0">
                    <pre className="text-[11px] font-mono text-gray-600 dark:text-gray-400 leading-relaxed whitespace-pre-wrap break-all">
{`<!-- Meta tags -->\n<title>${editorTitle || "Page Title"}</title>\n<meta name="description" content="${editorDesc || "Page description preview..."}">
${editorMetaRobots ? `<meta name="robots" content="${editorMetaRobots}">\n` : ''}${editorViewport ? `<meta name="viewport" content="${editorViewport}">\n` : ''}${editorCanonical ? `<link rel="canonical" href="${editorCanonical}">\n` : ''}${editorHreflang ? `<link rel="alternate" hreflang="..." href="${editorHreflang}">\n` : ''}${editorTwitterCard ? `<meta name="twitter:card" content="${editorTwitterCard}">\n` : ''}${editorOgTitle ? `<meta property="og:title" content="${editorOgTitle}">\n` : ''}${editorOgDesc ? `<meta property="og:description" content="${editorOgDesc}">\n` : ''}${editorOgImage ? `<meta property="og:image" content="${editorOgImage}">\n` : ''}`.trim()}
                    </pre>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
        </div>
      )}
    </div>
  );
}
